/**
 * Centralized query key factory for TanStack Query.
 *
 * Convention:
 *   - Each domain exports a key factory object
 *   - `.all` — base key for the domain (used for broad invalidation)
 *   - `.lists()` — key for list queries
 *   - `.detail(id)` — key for single-entity queries
 *   - `.byParam(param)` — key for filtered/parameterized queries
 *
 * Usage:
 *   queryClient.invalidateQueries(queryKeys.accounts.all)  → invalidates ALL account queries
 *   queryClient.invalidateQueries(queryKeys.accounts.lists()) → invalidates only list
 */

export const queryKeys = {
  accounts: {
    all: ['accounts'] as const,
    lists: () => [...queryKeys.accounts.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.accounts.all, 'detail', id] as const,
    withPartnerDomains: (partnerId: string) =>
      [...queryKeys.accounts.all, 'withPartnerDomains', partnerId] as const,
  },

  partners: {
    all: ['partners'] as const,
    lists: () => [...queryKeys.partners.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.partners.all, 'detail', id] as const,
  },

  roles: {
    all: ['roles'] as const,
    lists: () => [...queryKeys.roles.all, 'list'] as const,
  },

  domains: {
    all: ['domains'] as const,
  },

  offerings: {
    all: ['offerings'] as const,
    bitwerx: {
      all: ['offerings', 'bitwerx'] as const,
      lists: () => [...queryKeys.offerings.bitwerx.all, 'list'] as const,
      detail: (id: string) => [...queryKeys.offerings.bitwerx.all, 'detail', id] as const,
    },
    partner: {
      all: ['offerings', 'partner'] as const,
    },
  },

  plugins: {
    all: ['plugins'] as const,
    byOffering: (offeringName: string) =>
      [...queryKeys.plugins.all, 'offering', offeringName] as const,
    activeNonOffering: (offeringName: string) =>
      [...queryKeys.plugins.all, 'activeNonOffering', offeringName] as const,
  },

  missionControl: {
    all: ['missionControl'] as const,
    plugins: {
      all: ['missionControl', 'plugins'] as const,
      lists: (params?: { pimsFilter?: string; includeInactive?: boolean }) =>
        [...queryKeys.missionControl.plugins.all, 'list', params ?? {}] as const,
      detail: (id: string) => [...queryKeys.missionControl.plugins.all, 'detail', id] as const,
      versions: (pluginId: string) =>
        [...queryKeys.missionControl.plugins.all, 'versions', pluginId] as const,
    },
    versions: {
      all: ['missionControl', 'versions'] as const,
      lists: () => [...queryKeys.missionControl.versions.all, 'list'] as const,
      default: () => [...queryKeys.missionControl.versions.all, 'default'] as const,
    },
  },

  pims: {
    all: ['pims'] as const,
    lists: () => [...queryKeys.pims.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.pims.all, 'detail', id] as const,
  },

  apps: {
    all: ['apps'] as const,
    lists: () => [...queryKeys.apps.all, 'list'] as const,
  },

  instances: {
    all: ['instances'] as const,
    lists: () => [...queryKeys.instances.all, 'list'] as const,
    manifest: (id: string) => [...queryKeys.instances.all, 'manifest', id] as const,
  },

  billingAudit: {
    all: ['billingAudit'] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.billingAudit.all, 'list', params] as const,
  },

  configurations: {
    all: ['configurations'] as const,
    byPims: (pimsId: string) => [...queryKeys.configurations.all, 'pims', pimsId] as const,
  },
} as const;
